// FinanceClearanceUpdateForm.js
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Card,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
} from '@mui/material';
import { CloudUpload, Delete, Add, Close, Warning } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import MDButton from 'components/MDButton';
import { PUT, POST } from 'APIclient';
import properties from 'constants/ApplicationConstants';
import { read } from 'xlsx';

const AdditionalItemsSection = ({
  items,
  handleAdditionalItemChange,
  handleRemoveAdditionalItem,
  handleAddAdditionalItem,
  validationErrors,
  disabled,
}) => {
  return (
    <>
      {items.map((item, index) => (
        <Grid
          container
          spacing={2}
          key={index}
          sx={{ mt: 0, alignItems: 'center', borderBottom: '1px solid #e0e0e0', pb: 1 }}
        >
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="standard"
              placeholder="Enter particular"
              value={item.particular || item.itemName || ''}
              onChange={(e) => handleAdditionalItemChange(index, 'particular', e.target.value)}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth variant="standard" error={validationErrors[`additionalItem_${index}_status`]}>
              <InputLabel sx={{ mt: -1 }}>Select Status</InputLabel>
              <Select
                value={item.status || ''}
                onChange={(e) => handleAdditionalItemChange(index, 'status', e.target.value)}
                disabled={disabled}
              >
                {/* <MenuItem value="" disabled>Select Status</MenuItem> */}
                <MenuItem value="YES">YES</MenuItem>
                <MenuItem value="NO">NO</MenuItem>
                <MenuItem value="NA">NA</MenuItem>
              </Select>
              {validationErrors[`additionalItem_${index}_status`] && (
                <FormHelperText>Please select a status</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="standard"
              placeholder="Employee comment"
              value={item.employeeComment || ''}
              disabled
              sx={{
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                  backgroundColor: '#f5f5f5',
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="standard"
              placeholder="Enter department comment"
              value={item.departmentComment || ''}
              onChange={(e) => handleAdditionalItemChange(index, 'departmentComment', e.target.value)}
              disabled={disabled}
              error={validationErrors[`additionalItem_${index}_departmentComment`]}
              helperText={validationErrors[`additionalItem_${index}_departmentComment`] ? 'Department comment is required' : ''}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <IconButton
              size="small"
              onClick={() => handleRemoveAdditionalItem(index)}
              color="error"
              disabled={disabled}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Button
        variant="outlined"
        size="small"
        startIcon={<Add />}
        onClick={handleAddAdditionalItem}
        disabled={disabled}
        sx={{
          mt: 1,
          color: 'rgba(158, 97, 97, 0.93)',
          borderColor: 'black',
          '&:hover': {
            borderColor: 'black',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
      >
        Add New Row
      </Button>
    </>
  );
};

const FinanceClearanceUpdateForm = ({ open, onClose, clearanceData, onSuccess, readOnly = false }) => {
  // Loading states
  const [loading, setLoading] = useState(false); // generic loading for dialogs
  const [updateLoading, setUpdateLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);

  // Notifications
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackColor, setSnackColor] = useState('success');
  const [snackMessage, setSnackMessage] = useState('');

  // Approval / signature states
  const [isAlreadyApproved, setIsAlreadyApproved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [openSignatureDialog, setOpenSignatureDialog] = useState(false);
  const [openSimpleSignatureDialog, setOpenSimpleSignatureDialog] = useState(false);
  const [existingSignature, setExistingSignature] = useState(null);
  const [signatureChoice, setSignatureChoice] = useState('existing'); // 'existing' | 'new'
  const [newSignature, setNewSignature] = useState(null);

  const [validationErrors, setValidationErrors] = useState({});

  const empId = localStorage.getItem("empId") || "";
  const existingEmpId = clearanceData?.empId;

  const [formData, setFormData] = useState({
    empId: empId || '',
    travelReimbursement: 'NA',
    empTravelReimbursementComment: '',
    departmentTravelReimbursementComment: '',
    guesthouseReimbursement: 'NA',
    empGuesthouseReimbursementComment: '',
    departmentGuesthouseReimbursementComment: '',
    billsPayable: 'NA',
    empBillsPayableComment: '',
    departmentBillsPayableComment: '',
    taxationDue: 'NA',
    empTaxationDueComment: '',
    departmentTaxationDueComment: '',
    companyLoan: 'NA',
    empCompanyLoanComment: '',
    departmentCompanyLoanComment: '',
    additionalItems: [],
    clearanceDate: dayjs(),
  });

  // Initialize form when clearanceData arrives
  useEffect(() => {
    if (clearanceData) {
      const wasApproved = clearanceData.status?.toLowerCase() === 'approved' ||
        clearanceData.status?.toLowerCase() === 'cleared';
      setIsAlreadyApproved(wasApproved);

      setFormData({
        empId: empId || '',
        travelReimbursement: clearanceData?.travelReimbursementStatus || 'NA',
        empTravelReimbursementComment: clearanceData?.empTravelReimbursementComment || '',
        departmentTravelReimbursementComment: clearanceData?.departmentTravelReimbursementComment || '',
        guesthouseReimbursement: clearanceData?.guesthouseReimbursementStatus || 'NA',
        empGuesthouseReimbursementComment: clearanceData?.empGuesthouseReimbursementComment || '',
        departmentGuesthouseReimbursementComment: clearanceData?.departmentGuesthouseReimbursementComment || '',
        billsPayable: clearanceData?.billsPayableStatus || 'NA',
        empBillsPayableComment: clearanceData?.empBillsPayableComment || '',
        departmentBillsPayableComment: clearanceData?.departmentBillsPayableComment || '',
        taxationDue: clearanceData?.taxationDueStatus || 'NA',
        empTaxationDueComment: clearanceData?.empTaxationDueComment || '',
        departmentTaxationDueComment: clearanceData?.departmentTaxationDueComment || '',
        companyLoan: clearanceData?.companyLoanStatus || 'NA',
        empCompanyLoanComment: clearanceData?.empCompanyLoanComment || '',
        departmentCompanyLoanComment: clearanceData?.departmentCompanyLoanComment || '',
        additionalItems: (clearanceData?.additionalItems || []).map(item => ({
          // keep backend format (YES/NO/NA)
          particular: item.particular || item.itemName || '',
          status: item.status || 'NA',
          employeeComment: item.employeeComment || '',
          departmentComment: item.departmentComment || '',
        })),
        clearanceDate: clearanceData?.clearanceDate ? dayjs(clearanceData.clearanceDate) : dayjs(),
      });

      setExistingSignature(wasApproved ? clearanceData?.managerSignature : null);
      setHasChanges(false);
      setSignatureChoice('existing');
      setNewSignature(null);
      setValidationErrors({});
    }
  }, [clearanceData, empId]);

  // Generic change handlers
  const handleNestedChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    // clear field error
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const copy = { ...prev };
        delete copy[field];
        return copy;
      });
    }
  };

  const handleDateChange = (date) => {
    const validDate = date ? dayjs(date) : null;
    const formatted = validDate?.isValid() ? validDate.format('YYYY-MM-DD') : null;
    setFormData((prev) => ({ ...prev, clearanceDate: formatted }));
    setHasChanges(true);
  };

  const handleNewSignatureUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setSnackMessage('File size should not exceed 2MB');
        setSnackColor('error');
        setSnackOpen(true);
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewSignature(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveNewSignature = () => {
    setNewSignature(null);
  };

  const handleAddAdditionalItem = () => {
    setFormData((prev) => ({
      ...prev,
      additionalItems: [...prev.additionalItems, {
        particular: '',
        status: 'NA',
        employeeComment: '',
        departmentComment: ''
      }],
    }));
    setHasChanges(true);
  };

  const handleAdditionalItemChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      additionalItems: prev.additionalItems.map((it, i) =>
        i === index ? { ...it, [field]: value } : it
      ),
    }));
    setHasChanges(true);
    // clear additional item specific error
    const errorKey = `additionalItem_${index}_${field === 'departmentComment' ? 'departmentComment' : 'status'}`;
    if (validationErrors[errorKey]) {
      setValidationErrors(prev => {
        const copy = { ...prev };
        delete copy[errorKey];
        return copy;
      });
    }
  };

  const handleRemoveAdditionalItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      additionalItems: prev.additionalItems.filter((_, i) => i !== index),
    }));
    setHasChanges(true);
    // remove related errors
    setValidationErrors(prev => {
      const copy = { ...prev };
      delete copy[`additionalItem_${index}_status`];
      delete copy[`additionalItem_${index}_departmentComment`];
      return copy;
    });
  };

  // Validation - requires status and department comment for all main fields and additional items
  const validateForm = () => {
    const errors = {};

    const mainFields = [
      { key: 'travelReimbursement', commentKey: 'departmentTravelReimbursementComment' },
      { key: 'guesthouseReimbursement', commentKey: 'departmentGuesthouseReimbursementComment' },
      { key: 'billsPayable', commentKey: 'departmentBillsPayableComment' },
      { key: 'taxationDue', commentKey: 'departmentTaxationDueComment' },
      { key: 'companyLoan', commentKey: 'departmentCompanyLoanComment' },
    ];

    mainFields.forEach(({ key, commentKey }) => {
      if (!formData[key] || formData[key] === '') {
        errors[key] = true;
      }
      if (!formData[commentKey] || formData[commentKey].toString().trim() === '') {
        errors[commentKey] = true;
      }
    });

    // additionalItems validation
    (formData.additionalItems || []).forEach((item, index) => {
      if (!item.status || item.status === '') {
        errors[`additionalItem_${index}_status`] = true;
      }
      if (!item.departmentComment || item.departmentComment.toString().trim() === '') {
        errors[`additionalItem_${index}_departmentComment`] = true;
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Check for items with "NO" status - prevents approval if any items are marked NO
  const checkForNoStatusItems = () => {
    const noStatusItems = [];

    const mainFields = [
        { key: 'travelReimbursement', name: 'Travel Reimbursement' },
        { key: 'guesthouseReimbursement', name: 'Guesthouse Reimbursement' },
        { key: 'billsPayable', name: 'Bills Payable' },
        { key: 'taxationDue', name: 'Taxation Due' },
        { key: 'companyLoan', name: 'Company Loan' },
    ];

    mainFields.forEach(({ key, name }) => {
        if (formData[key] === 'NO') {
            noStatusItems.push(name);
        }
    });

    (formData.additionalItems || []).forEach((item, index) => {
        if (item.status === 'NO') {
            const itemName = item.particular || `Additional Item ${index + 1}`;
            noStatusItems.push(itemName);
        }
    });

    return noStatusItems;
};

  // Submit handler - default is update only; if isApprove true, handle update+approve flow
  const handleSubmit = async (e, isApprove = false) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!isApprove) {
      // Update only
      // if (!validateForm()) {
      //   setSnackMessage('Please fill all required fields');
      //   setSnackColor('error');
      //   setSnackOpen(true);
      //   return;
      // }
      await performUpdate(null, false);
      return;
    }


    // First check for "NO" status items
    const noStatusItems = checkForNoStatusItems();
    if (noStatusItems.length > 0) {
      const itemsList = noStatusItems.map((item, idx) => `${idx + 1}. ${item}`).join('\n');
      setSnackMessage(
        `Cannot approve clearance. The following items are marked as "NO":\n\n${itemsList}\n\nPlease resolve all pending items before approving.`
      );
      setSnackColor('error');
      setSnackOpen(true);
      return;
    }

    // Update & Approve flow
    if (!validateForm()) {
      setSnackMessage('Please fill all required fields');
      setSnackColor('error');
      setSnackOpen(true);
      return;
    }

    if (isAlreadyApproved) {
      // re-approval requires choice (existing/new) -> open re-approval dialog
      setOpenSignatureDialog(true);
    } else {
      // first-time approval -> simple signature upload dialog
      setOpenSimpleSignatureDialog(true);
    }
  };

  // performUpdate can optionally trigger performApproval after successful update
  const performUpdate = async (signatureForApproval = null, shouldApprove = false) => {
    // choose loading state
    if (shouldApprove) {
      setApproveLoading(true);
    } else {
      setUpdateLoading(true);
    }

    try {
      const jwtToken = window.sessionStorage.getItem('authToken') || window.sessionStorage.getItem('token');

      const updateData = {
        empId: empId || '',
        travelReimbursementStatus: formData.travelReimbursement,
        empTravelReimbursementComment: formData.empTravelReimbursementComment,
        departmentTravelReimbursementComment: formData.departmentTravelReimbursementComment,
        guesthouseReimbursementStatus: formData.guesthouseReimbursement,
        empGuesthouseReimbursementComment: formData.empGuesthouseReimbursementComment,
        departmentGuesthouseReimbursementComment: formData.departmentGuesthouseReimbursementComment,
        billsPayableStatus: formData.billsPayable,
        empBillsPayableComment: formData.empBillsPayableComment,
        departmentBillsPayableComment: formData.departmentBillsPayableComment,
        taxationDueStatus: formData.taxationDue,
        empTaxationDueComment: formData.empTaxationDueComment,
        departmentTaxationDueComment: formData.departmentTaxationDueComment,
        companyLoanStatus: formData.companyLoan,
        empCompanyLoanComment: formData.empCompanyLoanComment,
        departmentCompanyLoanComment: formData.departmentCompanyLoanComment,
        additionalItems: (formData.additionalItems || []).map(it => ({
          particular: it.particular,
          status: it.status,
          employeeComment: it.employeeComment,
          departmentComment: it.departmentComment,
        })),
        clearanceDate: formData.clearanceDate ? dayjs(formData.clearanceDate).format('YYYY-MM-DD') : null,
        // managerSignature is sent during approval step (POST approve), so keep null for update
        managerSignature: null,
      };

      const url = `api/exit/clearance/finance/${clearanceData.exitClearanceId}`;
      const response = await PUT(url, updateData, {
        headers: { Authorization: `Bearer ${jwtToken}` },
      });

      if (response.status === 200) {
        setSnackMessage('Finance clearance updated successfully');
        setSnackColor('success');
        setSnackOpen(true);

        if (shouldApprove && signatureForApproval) {
          // after successful update, trigger approval POST
          await performApproval(signatureForApproval);
        } else {
          // finish
          setTimeout(() => {
            onSuccess && onSuccess();
            onClose && onClose();
          }, 1200);
        }
      }
    } catch (error) {
      setSnackMessage(error?.response?.data?.message || 'Failed to update Finance clearance');
      setSnackColor('error');
      setSnackOpen(true);
    } finally {
      setUpdateLoading(false);
      setApproveLoading(false);
    }
  };

  const performApproval = async (signature) => {
    try {
      setLoading(true);
      const jwtToken = window.sessionStorage.getItem('authToken') || window.sessionStorage.getItem('token');
      const requestBody = { managerSignature: signature };
      const response = await POST(
        `api/exit/clearance/finance/${clearanceData.exitClearanceId}/approve`,
        requestBody,
        { headers: { Authorization: `Bearer ${jwtToken}` } }
      );

      if (response.status === 200) {
        setSnackMessage('Finance clearance updated and approved successfully');
        setSnackColor('success');
        setSnackOpen(true);
        setTimeout(() => {
          onSuccess && onSuccess();
          onClose && onClose();
        }, 1400);
      }
    } catch (error) {
      setSnackMessage(error?.response?.data?.message || 'Failed to approve Finance clearance');
      setSnackColor('error');
      setSnackOpen(true);
    } finally {
      setLoading(false);
      setApproveLoading(false);
      setUpdateLoading(false);
    }
  };

  // Signature dialog confirm handlers
  const handleSignatureDialogConfirm = async () => {
    // For re-approval (openSignatureDialog), user may choose existing or upload new
    if (signatureChoice === 'new' && !newSignature) {
      setSnackMessage('Please upload a new signature');
      setSnackColor('error');
      setSnackOpen(true);
      return;
    }
    setOpenSignatureDialog(false);
    const signatureToUse = signatureChoice === 'existing' ? existingSignature : newSignature;
    await performUpdate(signatureToUse, true);
  };

  const handleSignatureDialogCancel = () => {
    setOpenSignatureDialog(false);
    setSignatureChoice('existing');
    setNewSignature(null);
  };

  // Simple dialog confirm for first-time approval
  const handleSimpleSignatureConfirm = async () => {
    if (!newSignature) {
      setSnackMessage('Please upload your signature to approve');
      setSnackColor('error');
      setSnackOpen(true);
      return;
    }
    setOpenSimpleSignatureDialog(false);
    await performUpdate(newSignature, true);
  };

  const handleSimpleSignatureCancel = () => {
    setOpenSimpleSignatureDialog(false);
    setNewSignature(null);
  };

  const sectionCardStyle = {
    p: 2.5,
    mb: 2,
    backgroundColor: '#f5f7fa',
    borderRadius: 2,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper">
      <DialogTitle sx={{ p: 0 }}>
        <MDBox
          variant="gradient"
          sx={{ background: properties.colors.bgcolorbluethem }}
          borderRadius="lg"
          p={3}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Finance Clearance Form
          </MDTypography>
          <MDTypography variant="body2" color="white" opacity={0.8}>
            Update Finance clearance details and approve
          </MDTypography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: 'white',
            }}
          >
            <Close />
          </IconButton>
        </MDBox>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <MDBox pt={4} pb={3}>
          <Card>
            <MDBox pt={4} pb={3} px={3}>
              {/* Warning banner */}
              {isAlreadyApproved && (
                <Card
                  sx={{
                    p: 2,
                    mb: 3,
                    backgroundColor: '#fff3cd',
                    border: '2px solid #ffc107',
                  }}
                >
                  <MDBox display="flex" alignItems="center">
                    <Warning sx={{ color: '#ff9800', mr: 1 }} />
                    <MDTypography variant="body2" color="text" fontWeight="bold">
                      Notice: This clearance was previously approved. If you make changes, you will need to re-approve with a signature.
                    </MDTypography>
                  </MDBox>
                </Card>
              )}

              <Card
                sx={{
                  p: 2,
                  mb: 3,
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                }}
              >
                <MDTypography variant="body2" color="text" textAlign="center" fontWeight="medium">
                  An employee is required to return the Company&apos;s properties to the respective Department&apos;s before leaving the Company. He/She is instructed to bring the form to the various Departments as stated below to obtain Clearance before exit.
                </MDTypography>
              </Card>

              <form onSubmit={(e) => handleSubmit(e, false)}>
                <Grid container spacing={2}>
                  {/* Employee / Project / Exit info - same as original file */}
                  <Grid item xs={12}>
                    <Card sx={{ backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0', mb: 2 }}>
                      <MDBox p={3}>
                        <MDBox display="flex" alignItems="center" mb={2}>
                          <Box sx={{ width: 4, height: 24, backgroundColor: '#1A73E8', borderRadius: 1, mr: 1.5 }} />
                          <MDTypography variant="h6" fontWeight="bold" color="info">
                            Personal Information
                          </MDTypography>
                        </MDBox>
                        <Grid container spacing={2.5}>
                          <Grid item xs={12} md={3}>
                            <MDBox>
                              <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>
                                Name
                              </MDTypography>
                              <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                                {clearanceData?.employeeName || 'N/A'}
                              </MDTypography>
                            </MDBox>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <MDBox>
                              <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>
                                Employee ID
                              </MDTypography>
                              <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                                {clearanceData?.employeeId || 'N/A'}
                              </MDTypography>
                            </MDBox>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <MDBox>
                              <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>
                                Designation
                              </MDTypography>
                              <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                                {clearanceData?.designation || 'N/A'}
                              </MDTypography>
                            </MDBox>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <MDBox>
                              <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>
                                Technology
                              </MDTypography>
                              <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                                {clearanceData?.technology || 'N/A'}
                              </MDTypography>
                            </MDBox>
                          </Grid>
                        </Grid>
                      </MDBox>
                    </Card>

                    <Card sx={{ backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0', mb: 2 }}>
                      <MDBox p={3}>
                        <MDBox display="flex" alignItems="center" mb={2}>
                          <Box sx={{ width: 4, height: 24, backgroundColor: '#1A73E8', borderRadius: 1, mr: 1.5 }} />
                          <MDTypography variant="h6" fontWeight="bold" color="info">
                            Project Details
                          </MDTypography>
                        </MDBox>
                        <Grid container spacing={2.5}>
                          <Grid item xs={12} md={4}>
                            <MDBox>
                              <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>
                                Project Name
                              </MDTypography>
                              <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                                {clearanceData?.projectName || 'N/A'}
                              </MDTypography>
                            </MDBox>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <MDBox>
                              <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>
                                Reporting Manager
                              </MDTypography>
                              <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                                {clearanceData?.projectManager || 'N/A'}
                              </MDTypography>
                            </MDBox>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <MDBox>
                              <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>
                                Knowledge Transfer
                              </MDTypography>
                              <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                                {clearanceData?.knowledgeTransfer || 'N/A'}
                              </MDTypography>
                            </MDBox>
                          </Grid>
                        </Grid>
                      </MDBox>
                    </Card>

                    <Card sx={{ backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                      <MDBox p={3}>
                        <MDBox display="flex" alignItems="center" mb={2}>
                          <Box sx={{ width: 4, height: 24, backgroundColor: '#1A73E8', borderRadius: 1, mr: 1.5 }} />
                          <MDTypography variant="h6" fontWeight="bold" color="info">
                            Exit Information
                          </MDTypography>
                        </MDBox>
                        <Grid container spacing={2.5}>
                          <Grid item xs={12} md={4}>
                            <MDBox>
                              <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>
                                Date of Joining
                              </MDTypography>
                              <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                                {clearanceData?.dateOfJoining
                                  ? new Date(clearanceData.dateOfJoining).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
                                  : 'N/A'}
                              </MDTypography>
                            </MDBox>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <MDBox>
                              <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>
                                Date of Leaving
                              </MDTypography>
                              <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                                {clearanceData?.dateOfLeaving
                                  ? new Date(clearanceData.dateOfLeaving).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
                                  : 'N/A'}
                              </MDTypography>
                            </MDBox>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <MDBox>
                              <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>
                                Notice Period Served
                              </MDTypography>
                              <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                                {clearanceData?.noticePeriodServed || 'N/A'} days
                              </MDTypography>
                            </MDBox>
                          </Grid>
                        </Grid>
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card sx={{ p: 2, mb: 2, backgroundColor: '#e7f3ff', border: '1px solid #2196f3' }}>
                      <MDTypography variant="body2" color="text" fontWeight="medium">
                        Person in-charge is required to sign-off in the respective sections mentioned below. Advising team and Reporting Manager should sign after rest of the clearances.
                      </MDTypography>
                    </Card>
                  </Grid>

                  {/* Finance Clearance – Editable */}
                  <Grid item xs={12}>
                    <Card sx={sectionCardStyle}>
                      <MDTypography variant="h6" fontWeight="bold" color="text.primary">
                        Finance Clearance
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary">
                        Please verify all financial transactions.
                      </MDTypography>

                      <Grid container spacing={2} sx={{ mt: 1, alignItems: 'center' }}>
                        {/* Header */}
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2" fontWeight="bold">Particulars</Typography>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Typography variant="body2" fontWeight="bold">Status<span style={{ color: '#d32f2f' }}>*</span></Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2" fontWeight="bold">Employee Comment</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="body2" fontWeight="bold">Department Comment<span style={{ color: '#d32f2f' }}>*</span></Typography>
                        </Grid>

                        {/* Travel Reimbursement */}
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2">Travel Reimbursement</Typography>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth variant="standard" error={validationErrors.travelReimbursement}>
                            <InputLabel sx={{ mt: -1 }}>Select Status</InputLabel>
                            <Select
                              value={formData.travelReimbursement || ''}
                              onChange={(e) => handleNestedChange('travelReimbursement', e.target.value)}
                              disabled={readOnly}
                            >
                              {/* <MenuItem value="" disabled>Select Status</MenuItem> */}
                              <MenuItem value="YES">YES</MenuItem>
                              <MenuItem value="NO">NO</MenuItem>
                              <MenuItem value="NA">NA</MenuItem>
                            </Select>
                            {validationErrors.travelReimbursement && (
                              <FormHelperText>Please select a status</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            variant="standard"
                            value={formData.empTravelReimbursementComment}
                            disabled
                            placeholder="Employee comment"
                            sx={{
                              '& .MuiInputBase-input.Mui-disabled': {
                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                                backgroundColor: '#f5f5f5',
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            variant="standard"
                            value={formData.departmentTravelReimbursementComment}
                            onChange={(e) => handleNestedChange('departmentTravelReimbursementComment', e.target.value)}
                            placeholder="Enter department comment"
                            disabled={readOnly}
                            error={validationErrors.departmentTravelReimbursementComment}
                            helperText={validationErrors.departmentTravelReimbursementComment ? 'Department comment is required' : ''}
                          />
                        </Grid>

                        {/* Guesthouse Reimbursement */}
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2">Guesthouse Reimbursement</Typography>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth variant="standard" error={validationErrors.guesthouseReimbursement}>
                            <InputLabel sx={{ mt: -1 }}>Select Status</InputLabel>
                            <Select
                              value={formData.guesthouseReimbursement || ''}
                              onChange={(e) => handleNestedChange('guesthouseReimbursement', e.target.value)}
                              disabled={readOnly}
                            >
                              {/* <MenuItem value="" disabled>Select Status</MenuItem> */}
                              <MenuItem value="YES">YES</MenuItem>
                              <MenuItem value="NO">NO</MenuItem>
                              <MenuItem value="NA">NA</MenuItem>
                            </Select>
                            {validationErrors.guesthouseReimbursement && (
                              <FormHelperText>Please select a status</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            variant="standard"
                            value={formData.empGuesthouseReimbursementComment}
                            disabled
                            placeholder="Employee comment"
                            sx={{
                              '& .MuiInputBase-input.Mui-disabled': {
                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                                backgroundColor: '#f5f5f5',
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            variant="standard"
                            value={formData.departmentGuesthouseReimbursementComment}
                            onChange={(e) => handleNestedChange('departmentGuesthouseReimbursementComment', e.target.value)}
                            placeholder="Enter department comment"
                            disabled={readOnly}
                            error={validationErrors.departmentGuesthouseReimbursementComment}
                            helperText={validationErrors.departmentGuesthouseReimbursementComment ? 'Department comment is required' : ''}
                          />
                        </Grid>

                        {/* Bills Payable */}
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2">Bills Payable</Typography>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth variant="standard" error={validationErrors.billsPayable}>
                            <InputLabel sx={{ mt: -1 }}>Select Status</InputLabel>
                            <Select
                              value={formData.billsPayable || ''}
                              onChange={(e) => handleNestedChange('billsPayable', e.target.value)}
                              disabled={readOnly}
                            >
                              {/* <MenuItem value="" disabled>Select Status</MenuItem> */}
                              <MenuItem value="YES">YES</MenuItem>
                              <MenuItem value="NO">NO</MenuItem>
                              <MenuItem value="NA">NA</MenuItem>
                            </Select>
                            {validationErrors.billsPayable && (
                              <FormHelperText>Please select a status</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            variant="standard"
                            value={formData.empBillsPayableComment}
                            disabled
                            placeholder="Employee comment"
                            sx={{
                              '& .MuiInputBase-input.Mui-disabled': {
                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                                backgroundColor: '#f5f5f5',
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            variant="standard"
                            value={formData.departmentBillsPayableComment}
                            onChange={(e) => handleNestedChange('departmentBillsPayableComment', e.target.value)}
                            placeholder="Enter department comment"
                            disabled={readOnly}
                            error={validationErrors.departmentBillsPayableComment}
                            helperText={validationErrors.departmentBillsPayableComment ? 'Department comment is required' : ''}
                          />
                        </Grid>

                        {/* Taxation Due */}
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2">Taxation Due</Typography>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth variant="standard" error={validationErrors.taxationDue}>
                            <InputLabel sx={{ mt: -1 }}>Select Status</InputLabel>
                            <Select
                              value={formData.taxationDue || ''}
                              onChange={(e) => handleNestedChange('taxationDue', e.target.value)}
                              disabled={readOnly}
                            >
                              {/* <MenuItem value="" disabled>Select Status</MenuItem> */}
                              <MenuItem value="YES">YES</MenuItem>
                              <MenuItem value="NO">NO</MenuItem>
                              <MenuItem value="NA">NA</MenuItem>
                            </Select>
                            {validationErrors.taxationDue && (
                              <FormHelperText>Please select a status</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            variant="standard"
                            value={formData.empTaxationDueComment}
                            disabled
                            placeholder="Employee comment"
                            sx={{
                              '& .MuiInputBase-input.Mui-disabled': {
                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                                backgroundColor: '#f5f5f5',
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            variant="standard"
                            value={formData.departmentTaxationDueComment}
                            onChange={(e) => handleNestedChange('departmentTaxationDueComment', e.target.value)}
                            placeholder="Enter department comment"
                            disabled={readOnly}
                            error={validationErrors.departmentTaxationDueComment}
                            helperText={validationErrors.departmentTaxationDueComment ? 'Department comment is required' : ''}
                          />
                        </Grid>

                        {/* Company Loan */}
                        <Grid item xs={12} md={3}>
                          <Typography variant="body2">Company Loan</Typography>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <FormControl fullWidth variant="standard" error={validationErrors.companyLoan}>
                            <InputLabel sx={{ mt: -1 }}>Select Status</InputLabel>
                            <Select
                              value={formData.companyLoan || ''}
                              onChange={(e) => handleNestedChange('companyLoan', e.target.value)}
                              disabled={readOnly}
                            >
                              {/* <MenuItem value="" disabled>Select Status</MenuItem> */}
                              <MenuItem value="YES">YES</MenuItem>
                              <MenuItem value="NO">NO</MenuItem>
                              <MenuItem value="NA">NA</MenuItem>
                            </Select>
                            {validationErrors.companyLoan && (
                              <FormHelperText>Please select a status</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            variant="standard"
                            value={formData.empCompanyLoanComment}
                            disabled
                            placeholder="Employee comment"
                            sx={{
                              '& .MuiInputBase-input.Mui-disabled': {
                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                                backgroundColor: '#f5f5f5',
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            variant="standard"
                            value={formData.departmentCompanyLoanComment}
                            onChange={(e) => handleNestedChange('departmentCompanyLoanComment', e.target.value)}
                            placeholder="Enter department comment"
                            disabled={readOnly}
                            error={validationErrors.departmentCompanyLoanComment}
                            helperText={validationErrors.departmentCompanyLoanComment ? 'Department comment is required' : ''}
                          />
                        </Grid>

                        {/* Additional Items */}
                        <Grid item xs={12}>
                          <AdditionalItemsSection
                            items={formData.additionalItems}
                            handleAdditionalItemChange={handleAdditionalItemChange}
                            handleRemoveAdditionalItem={handleRemoveAdditionalItem}
                            handleAddAdditionalItem={handleAddAdditionalItem}
                            validationErrors={validationErrors}
                            disabled={readOnly}
                          />
                        </Grid>

                        {/* Clearance Date */}
                        <Grid item xs={12} md={2}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              format="DD/MM/YYYY"
                              label="Date"
                              value={formData.clearanceDate ? dayjs(formData.clearanceDate) : null}
                              // read only like original
                              slotProps={{
                                textField: { variant: 'standard', fullWidth: true },
                              }}
                              readOnly
                            />
                          </LocalizationProvider>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>

                  {/* Approved Badge */}
                  {existingSignature && (
                    <Grid item xs={12}>
                      <Card sx={{ p: 3, mb: 3, backgroundColor: '#e8f5e8', border: '2px solid #4caf50', borderRadius: 2 }}>
                        <MDBox display="flex" alignItems="center" justifyContent="space-between">
                          <MDBox display="flex" alignItems="center">
                            <CheckCircleIcon sx={{ fontSize: 40, color: '#2e7d32', mr: 2 }} />
                            <MDBox>
                              <MDTypography variant="h6" fontWeight="bold" color="success.dark">Clearance Approved</MDTypography>
                              <MDTypography variant="body2">This Administration clearance has been approved.</MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #a5d6a7' }}>
                            <img
                              src={existingSignature.startsWith('data:') ? existingSignature : `data:image/png;base64,${existingSignature}`}
                              alt="Signature" style={{ maxHeight: '80px', maxWidth: '250px', objectFit: 'contain' }}
                            />
                          </MDBox>
                        </MDBox>
                      </Card>
                    </Grid>
                  )}

                  {/* Submit Buttons */}
                  {!readOnly && (
                  <Grid item xs={12}>
                    <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                      <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={updateLoading || approveLoading}>
                        Cancel
                      </MDButton>

                      {/* Update Only Button */}
                      <MDButton
                        variant="gradient"
                        color="info"
                        type="submit"
                        disabled={updateLoading || approveLoading || readOnly}
                        sx={{ background: properties.colors.bgcolorbluethem, ml: 2 }}
                      >
                        {updateLoading ? (
                          <>
                            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                            Updating...
                          </>
                        ) : (
                          'Update Finance Clearance Form'
                        )}
                      </MDButton>

                      {/* Update & Approve Button */}
                      <MDButton
                        variant="gradient"
                        color="success"
                        onClick={(e) => handleSubmit(e, true)}
                        disabled={updateLoading || approveLoading || readOnly}
                        sx={{
                          background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                          ml: 2,
                        }}
                      >
                        {approveLoading ? (
                          <>
                            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                            Approving...
                          </>
                        ) : (
                          'Update & Approve'
                        )}
                      </MDButton>
                    </DialogActions>
                  </Grid>
                  )}
                </Grid>
              </form>

              <Snackbar
                open={snackOpen}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                autoHideDuration={6000} // Increased from 3000 to give more time to read
                onClose={() => setSnackOpen(false)}
              >
                <Alert
                  onClose={() => setSnackOpen(false)}
                  severity={snackColor === 'success' ? 'success' : snackColor === 'warning' ? 'warning' : 'error'}
                  variant="filled"
                  sx={{
                    width: '100%',
                    color: '#fff',
                    whiteSpace: 'pre-line', // Allow line breaks
                    maxWidth: '500px' // Wider for better readability
                  }}
                >
                  {snackMessage}
                </Alert>
              </Snackbar>
            </MDBox>
          </Card>

          <Box mt={2} mb={4} textAlign="center">
            <Typography variant="caption" color="text.secondary">
              BNT SOFT PVT. LTD. Office No. 301-304, &quot;A&quot; Building,
              Pune IT Park, 34, Aundh Road, Bhau Patil Marg, Bopodi, Pune-411020,
              Maharashtra, India.
            </Typography>
          </Box>
        </MDBox>
      </DialogContent>

      {/* Re-approval Signature Dialog (existing vs new) */}
      <Dialog open={openSignatureDialog} onClose={() => setOpenSignatureDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            py: 3,
            position: 'relative',
          }}
        >
          <MDBox>
            <MDBox display="flex" alignItems="center" mb={1}>
              <Warning sx={{ fontSize: 32, mr: 1.5 }} />
              <MDTypography variant="h4" fontWeight="bold" color="white">
                Re-approval Required
              </MDTypography>
            </MDBox>
            <MDTypography variant="body2" color="white" sx={{ opacity: 0.95, ml: 5.5 }}>
              Changes detected - signature verification needed
            </MDTypography>
          </MDBox>
          <IconButton
            onClick={handleSignatureDialogCancel}
            size="small"
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.3)',
              }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 4, backgroundColor: '#f8f9fa' }}>
          {/* Alert Message */}
          <Alert
            severity="warning"
            icon={<Warning />}
            sx={{
              mb: 4,
              borderRadius: 2,
              border: '1px solid #ffc107',
              '& .MuiAlert-icon': {
                fontSize: 28,
              }
            }}
          >
            <Typography variant="body1" fontWeight="medium" gutterBottom>
              This clearance was previously approved
            </Typography>
            <Typography variant="body2">
              Since you&apos;ve made changes to the clearance data, you need to re-approve it with a valid signature to maintain audit compliance.
            </Typography>
          </Alert>

          {/* Conditional Rendering based on Employee ID match */}
          {existingSignature && existingEmpId === empId ? (
            // Case 1: Same employee - Show choice between existing and new signature
            <>
              {/* Show existing signature */}
              <Card
                sx={{
                  mb: 3,
                  p: 3,
                  backgroundColor: 'white',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
              >
                <MDBox display="flex" alignItems="center" mb={2}>
                  <Box
                    sx={{
                      width: 6,
                      height: 24,
                      backgroundColor: '#667eea',
                      borderRadius: 1,
                      mr: 1.5
                    }}
                  />
                  <Typography variant="h6" fontWeight="bold" color="text.primary">
                    Your Previous Signature
                  </Typography>
                </MDBox>

                <Box
                  sx={{
                    display: 'inline-block',
                    p: 2.5,
                    border: '2px solid #e0e0e0',
                    borderRadius: 2,
                    backgroundColor: '#fafafa',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  }}
                >
                  <img
                    src={existingSignature.startsWith('data:')
                      ? existingSignature
                      : `data:image/png;base64,${existingSignature}`
                    }
                    alt="Existing Signature"
                    style={{
                      maxWidth: '350px',
                      maxHeight: '150px',
                      display: 'block',
                    }}
                  />
                </Box>
              </Card>

              {/* Signature choice */}
              <Card
                sx={{
                  p: 3,
                  backgroundColor: 'white',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
              >
                <MDBox display="flex" alignItems="center" mb={2}>
                  <Box
                    sx={{
                      width: 6,
                      height: 24,
                      backgroundColor: '#667eea',
                      borderRadius: 1,
                      mr: 1.5
                    }}
                  />
                  <Typography variant="h6" fontWeight="bold" color="text.primary">
                    Choose Signature Option
                  </Typography>
                </MDBox>

                <RadioGroup
                  value={signatureChoice}
                  onChange={(e) => setSignatureChoice(e.target.value)}
                >
                  <FormControlLabel
                    value="existing"
                    control={<Radio sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }} />}
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="body1" fontWeight="medium">
                          Use existing signature
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Reuse the signature shown above
                        </Typography>
                      </Box>
                    }
                    sx={{
                      mb: 2,
                      p: 2,
                      border: signatureChoice === 'existing' ? '2px solid #667eea' : '2px solid #e0e0e0',
                      borderRadius: 2,
                      backgroundColor: signatureChoice === 'existing' ? '#f3f4ff' : 'transparent',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#667eea',
                        backgroundColor: '#f3f4ff',
                      }
                    }}
                  />
                  <FormControlLabel
                    value="new"
                    control={<Radio sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }} />}
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="body1" fontWeight="medium">
                          Upload new signature
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Provide a fresh signature image
                        </Typography>
                      </Box>
                    }
                    sx={{
                      p: 2,
                      border: signatureChoice === 'new' ? '2px solid #667eea' : '2px solid #e0e0e0',
                      borderRadius: 2,
                      backgroundColor: signatureChoice === 'new' ? '#f3f4ff' : 'transparent',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#667eea',
                        backgroundColor: '#f3f4ff',
                      }
                    }}
                  />
                </RadioGroup>

                {/* New signature upload section */}
                {signatureChoice === 'new' && (
                  <Box sx={{ mt: 3, p: 3, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px dashed #667eea' }}>
                    {newSignature ? (
                      <Box>
                        <MDBox display="flex" alignItems="center" mb={2}>
                          <CheckCircleIcon sx={{ color: '#4caf50', mr: 1 }} />
                          <Typography variant="body1" color="success.main" fontWeight="bold">
                            New signature uploaded successfully!
                          </Typography>
                        </MDBox>
                        <Box
                          sx={{
                            display: 'inline-block',
                            p: 2.5,
                            border: '3px solid #4caf50',
                            borderRadius: 2,
                            backgroundColor: '#f1f8f4',
                            position: 'relative',
                            boxShadow: '0 4px 12px rgba(76,175,80,0.2)',
                          }}
                        >
                          <img
                            src={newSignature}
                            alt="New Signature"
                            style={{
                              maxWidth: '350px',
                              maxHeight: '150px',
                              display: 'block',
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={handleRemoveNewSignature}
                            sx={{
                              position: 'absolute',
                              top: -12,
                              right: -12,
                              bgcolor: 'white',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              '&:hover': {
                                bgcolor: '#ffebee',
                              }
                            }}
                          >
                            <Delete fontSize="small" color="error" />
                          </IconButton>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <CloudUpload sx={{ fontSize: 56, color: '#667eea', mb: 2 }} />
                        <Typography variant="h6" color="text.primary" mb={1} fontWeight="medium">
                          Upload Your Signature
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={3}>
                          Accepted formats: JPG, PNG, GIF (Max 2MB)
                        </Typography>
                        <Button
                          variant="contained"
                          component="label"
                          startIcon={<CloudUpload />}
                          sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: '#fff',
                            py: 1.5,
                            px: 4,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 600,
                            boxShadow: '0 4px 12px rgba(102,126,234,0.4)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                              boxShadow: '0 6px 16px rgba(102,126,234,0.5)',
                            },
                          }}
                        >
                          Choose File
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleNewSignatureUpload}
                          />
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Card>
            </>
          ) : (
            // Case 2: Different employee - Only show new signature upload
            <>
              <Alert
                severity="info"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  backgroundColor: '#e3f2fd',
                  border: '1px solid #2196f3',
                }}
              >
                <Typography variant="body2" fontWeight="medium">
                  Previous signature cannot be reused as it belongs to a different user. Please upload your signature to proceed.
                </Typography>
              </Alert>

              <Card
                sx={{
                  p: 3,
                  backgroundColor: 'white',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  border: '2px dashed #667eea',
                }}
              >
                {newSignature ? (
                  <Box>
                    <MDBox display="flex" alignItems="center" mb={2}>
                      <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28, mr: 1.5 }} />
                      <Typography variant="h6" color="success.main" fontWeight="bold">
                        Signature uploaded successfully!
                      </Typography>
                    </MDBox>
                    <Box
                      sx={{
                        display: 'inline-block',
                        p: 2.5,
                        border: '3px solid #4caf50',
                        borderRadius: 2,
                        backgroundColor: '#f1f8f4',
                        position: 'relative',
                        boxShadow: '0 4px 12px rgba(76,175,80,0.2)',
                      }}
                    >
                      <img
                        src={newSignature}
                        alt="New Signature"
                        style={{
                          maxWidth: '350px',
                          maxHeight: '150px',
                          display: 'block',
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={handleRemoveNewSignature}
                        sx={{
                          position: 'absolute',
                          top: -12,
                          right: -12,
                          bgcolor: 'white',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          '&:hover': {
                            bgcolor: '#ffebee',
                          }
                        }}
                      >
                        <Delete fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <CloudUpload sx={{ fontSize: 64, color: '#667eea', mb: 2 }} />
                    <Typography variant="h5" color="text.primary" mb={1} fontWeight="bold">
                      Upload Your Signature
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={3}>
                      Please provide your signature to re-approve this clearance
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mb={3}>
                      Accepted formats: JPG, PNG, GIF • Maximum size: 2MB
                    </Typography>
                    <Button
                      variant="contained"
                      component="label"
                      startIcon={<CloudUpload />}
                      sx={{
                        color: '#fff',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        py: 1.5,
                        px: 5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        boxShadow: '0 4px 12px rgba(102,126,234,0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                          boxShadow: '0 6px 16px rgba(102,126,234,0.5)',
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Choose Signature File
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleNewSignatureUpload}
                      />
                    </Button>
                  </Box>
                )}
              </Card>
            </>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            backgroundColor: 'white',
            borderTop: '1px solid #e0e0e0',
            gap: 2,
          }}
        >
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={handleSignatureDialogCancel}
            sx={{
              borderWidth: 2,
              borderColor: '#9e9e9e',
              color: '#616161',
              px: 3,
              py: 1,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              '&:hover': {
                borderWidth: 2,
                borderColor: '#757575',
                backgroundColor: '#f5f5f5',
              }
            }}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleSignatureDialogConfirm}
            disabled={
              loading ||
              (existingEmpId === empId && signatureChoice === 'new' && !newSignature) ||
              (existingEmpId !== empId && !newSignature)
            }
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              px: 4,
              py: 1,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(102,126,234,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                boxShadow: '0 6px 16px rgba(102,126,234,0.5)',
              },
              '&:disabled': {
                background: '#e0e0e0',
                color: '#9e9e9e',
              }
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Processing...
              </>
            ) : (
              <>
                <CheckCircleIcon sx={{ mr: 1, fontSize: 20 }} />
                Confirm & Re-approve
              </>
            )}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Simple Signature Dialog - First Time Approval */}
      <Dialog open={openSimpleSignatureDialog} onClose={handleSimpleSignatureCancel} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: '#1976d2', color: '#fff' }}>
          <Typography variant="h6">Approve Finance Clearance</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            {newSignature ? (
              <Box>
                <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                <img src={newSignature} alt="Signature" style={{ maxHeight: 120 }} />
                <IconButton onClick={handleRemoveNewSignature} color="error">
                  <Delete />
                </IconButton>
              </Box>
            ) : (
              <>
                <CloudUpload sx={{ fontSize: 64, color: '#667eea', mb: 2 }} />
                <Typography variant="h6" gutterBottom>Upload Your Signature</Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Required to approve this clearance
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<CloudUpload />}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                  }}
                >
                  Choose Signature File
                  <input type="file" hidden accept="image/*" onChange={handleNewSignatureUpload} />
                </Button>
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, backgroundColor: 'white', borderTop: '1px solid #e0e0e0', gap: 2 }}>
          <MDButton variant="outlined" color="secondary" onClick={handleSimpleSignatureCancel}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleSimpleSignatureConfirm}
            disabled={loading || !newSignature}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Processing...
              </>
            ) : (
              <>
                <CheckCircleIcon sx={{ mr: 1 }} />
                Confirm & Approve
              </>
            )}
          </MDButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default FinanceClearanceUpdateForm;